<template>
    <b-card-body>
        <!-- Table Container Card -->
        <b-card no-body class="mb-0" data-test="secretary-table">
            <b-card-header tag="h1" class="border-bottom">
                <h1 class="mb-0">Secretary</h1>
                
                <div v-if="shouldShowCreateBtn" class="d-flex align-items-center justify-content-end">
                    <b-button
                        variant="primary"
                        class="btn btn-sm"
                        data-test ="add-a-new-secretary"
                        @click="add()"
                    >
                        <span class="d-flex align-items-center text-nowrap">
                            Add <span class="d-none d-lg-block" style="text-indent: 4px;">a new secretary</span>
                        <vector data-name="add" data-class="no-path-fill ml-.5" />
                        </span>
                    </b-button>
                </div>
            </b-card-header>

            <b-card-body>
                <div class="my-2 filter-and-search">
                    <!-- Table Top -->
                    <b-row>
                        <!-- Per Page -->
                        <b-col
                            cols="12"
                            md="6"
                            class="
                                d-flex
                                align-items-center
                                justify-content-start
                                mb-1 mb-md-0
                                show-entries-select-box
                            "
                        >
                            <label class="show">Show</label>
                            <v-select
                                v-model="perPage"
                                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                                :options="perPageOptions"
                                :clearable="false"
                                class="per-page-selector d-inline-block mx-50"
                            />
                            <label>entries</label>
                        </b-col>

                        <!-- FILTERS -->
                        <b-col class="table-search-input" cols="12" md="6">
                            <b-row class="justify-content-end">
                                <div
                                    class="
                                        d-flex
                                        align-items-center
                                        justify-content-end
                                    "
                                >
                                    <b-form-input
                                        v-model="searchQuery"
                                        class="d-inline-block mr-1"
                                        placeholder="Search..."
                                        data-test="search"
                                    />
                                </div>
                            </b-row>
                        </b-col>
                    </b-row>
                </div>

                <b-table
                    ref="refSecretaryListTable"
                    class="position-relative"
                    :items="secretaries"
                    :fields="tableColumns"
                    :current-page="currentPage"
                    :per-page="perPage"
                    responsive
                    primary-key="id"
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="isSortDirDesc"
                    busy.sync="isBusy"
                    :filter="searchQuery"
                >
                    <template  #cell(index)="data" >
                        {{ data.index + 1 }}
                    </template>

                    <!-- Column: Actions -->
                    <template #cell(actions)= "data">
                        <div class="cursor-pointer">
                            <div v-if="isUserAdmin">
                                <b-dropdown size="sm" menu-class="" dropright variant="link" toggle-class="text-decoration-none" no-caret>
                                    <template #button-content>
                                        <feather-icon icon="MoreVerticalIcon"/>
                                    </template>

                                    <b-dropdown-item
                                        :data-test="`view-${data.item.email}`"
                                        @click="view(data)"
                                    >
                                        <div class="d-flex align-items-center">
                                            <feather-icon icon="EyeIcon" />
                                            <span style="padding-top: .1em; padding-left: .5em">View</span>
                                        </div>
                                    </b-dropdown-item>

                                    <b-dropdown-item
                                        :data-test="`edit-${data.item.email}`"
                                        @click="edit(data)"
                                    >
                                        <div class="d-flex align-items-center">
                                            <feather-icon icon="EditIcon" />
                                            <span style="padding-top: .1em; padding-left: .5em">Edit</span>
                                        </div>
                                    </b-dropdown-item>
                                    
                                    <b-dropdown-item
                                        :data-test="`delete-${data.item.email}`"
                                        @click="remove(data)"
                                    >
                                        <div class="d-flex align-items-center">
                                            <feather-icon icon="TrashIcon" />
                                            <span style="padding-top: .1em; padding-left: .5em">Delete</span>
                                        </div>
                                    </b-dropdown-item>
                                </b-dropdown>
                            </div>

                            <div 
                                v-else 
                                :data-test="`view-${data.item.email}`"
                                class="d-flex align-items-center justify-content-end" 
                                @click="view(data)"
                                >
                                <feather-icon icon="EyeIcon" />
                                <span style="padding-top: .1em; padding-left: .5em">View</span>
                            </div>
                        </div>
                    </template>
                </b-table>

                <loader v-if="isLoading" data-type="Table" class="custom-loading" />

                <div class="mx-2 mb-2">
                    <b-row>
                        <b-col
                            cols="12"
                            sm="6"
                            class="
                                d-flex
                                align-items-center
                                justify-content-center justify-content-sm-start
                            "
                        >
                            <span class="text-muted"
                                >Showing {{ dataMeta.from }} to {{ dataMeta.to }} of
                                {{ dataMeta.of }} entries</span
                            >
                        </b-col>
                        <!-- Pagination -->
                        <b-col
                            cols="12"
                            sm="6"
                            class="
                                d-flex
                                align-items-center
                                justify-content-center justify-content-sm-end
                            "
                        >
                            <b-pagination
                                v-model="currentPage"
                                :total-rows="totalSecretaries"
                                :per-page="perPage"
                                first-number
                                last-number
                                class="mb-0 mt-1 mt-sm-0"
                                prev-class="prev-item"
                                next-class="next-item"
                            >
                                <template #prev-text>
                                    <feather-icon
                                        icon="ChevronLeftIcon"
                                        size="18"
                                    />
                                </template>
                                <template #next-text>
                                    <feather-icon
                                        icon="ChevronRightIcon"
                                        size="18"
                                    />
                                </template>
                            </b-pagination>
                        </b-col>
                    </b-row>
                </div>
            </b-card-body>
        </b-card>
    </b-card-body>
</template>

<script>
import {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BFormInput,
    BTable,
    BPagination,
    BDropdown,
    BDropdownItem,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { ref } from '@vue/composition-api';
import { mapGetters } from "vuex";
import { COMPANY } from "@/constants/config";

export default {
    components: {
        BRow,
        BCol,
        BCard,
        BTable,
        vSelect,
        BButton,
        BDropdown,
        BCardBody,
        BFormInput,
        BPagination,
        BCardHeader,
        BDropdownItem,
    },

    props: {
        payloadForSecretaryTable: {
            type: Object,
            required: true
        }
    },

    computed: {
        ...mapGetters({
            isUserAdmin: "auth/isAdmin",
        }),

        shouldShowCreateBtn() {
            if(this.isUserAdmin && this.companyStatus !== COMPANY.STATUS_PRE_INCORPORATED) 
                return true;
            return false;
        },
    },
    
    methods:{
        add(){
            this.$emit('add');
        },

        view(data) {
            this.$emit('view', data, "view");
        },

        edit(data) {
            this.$emit('edit', data, "edit");
        },

        remove(data) {
            this.$emit('remove', data);
        }
    },

    setup(props) {
        const filterMode = ref(false);

        const {
            sortBy,
            isBusy,
            perPage,
            dataMeta,
            isLoading,
            currentPage,
            searchQuery,
            secretaries,
            refetchData,
            tableColumns,
            isSortDirDesc,
            perPageOptions,
            totalSecretaries,
            fetchSecretaries,
            refSecretaryListTable,
        } = props.payloadForSecretaryTable;

        return {
            sortBy,
            perPage,
            dataMeta,
            searchQuery,
            currentPage,
            tableColumns,
            isSortDirDesc,
            perPageOptions,
            refSecretaryListTable,

            secretaries,
            refetchData,
            fetchSecretaries,
            totalSecretaries,

            // UI
            isBusy,
            isLoading,
            filterMode,
        };
    }
};
</script>

<style lang="scss" scoped>
    .table-responsive {
        overflow-x: inherit;
    }
</style>